const API_KEY = import.meta.env.VITE_GOOGLE_TRANSLATE_API_KEY;
const CACHE_KEY = 'gtranslate_';
const MAX_CACHE_ENTRIES = 500;

// In-memory cache: { key: translatedText }
let memoryCache = {};
// localStorage cache for persistence across reloads
function getCacheKey(text, target) {
  return CACHE_KEY + target + '_' + String(text).slice(0, 80).replace(/[^a-zA-Z0-9]/g, '_');
}

function getCached(text, target) {
  const key = getCacheKey(text, target);
  if (memoryCache[key] !== undefined) return memoryCache[key];
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      memoryCache[key] = stored;
      return stored;
    }
  } catch (_) {}
  return null;
}

function setCache(text, target, translated) {
  const key = getCacheKey(text, target);
  memoryCache[key] = translated;
  try {
    const keys = Object.keys(memoryCache);
    if (keys.length > MAX_CACHE_ENTRIES) {
      const toRemove = keys.slice(0, keys.length - MAX_CACHE_ENTRIES);
      toRemove.forEach((k) => {
        localStorage.removeItem(k);
        delete memoryCache[k];
      });
    }
    localStorage.setItem(key, translated);
  } catch (_) {}
}

export async function translateText(text, target = 'en') {
  if (!text || !String(text).trim()) return text;
  if (target === 'en') return text;

  const trimmed = String(text).trim();
  const cached = getCached(trimmed, target);
  if (cached) return cached;

  if (!API_KEY) {
    if (import.meta.env.DEV && !window.__gtranslateKeyWarned) {
      window.__gtranslateKeyWarned = true;
      console.warn(
        '[Google Translate] VITE_GOOGLE_TRANSLATE_API_KEY is not set. Add it to .env and restart the dev server. Translation will show original text.'
      );
    }
    return trimmed;
  }

  try {
    const res = await fetch(
      `https://translation.googleapis.com/language/translate/v2?key=${API_KEY}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          q: trimmed,
          target,
          source: 'en',
          format: 'text',
        }),
      }
    );

    const data = await res.json();

    if (!res.ok || data.error) {
      const errMsg = data?.error?.message || data?.error?.code || res.status;
      console.warn('[Google Translate] API error:', errMsg, data?.error);
      return trimmed;
    }

    const translated = data?.data?.translations?.[0]?.translatedText ?? trimmed;
    setCache(trimmed, target, translated);
    return translated;
  } catch (e) {
    console.warn('[Google Translate] Request failed:', e);
    return trimmed;
  }
}
